import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError, BehaviorSubject } from 'rxjs';
import { tap, catchError, finalize, shareReplay, map } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { StorageService } from '../storage/storage.service';
import { TokenResponse } from '../../../domain/interfaces/auth/auth.interface';


const TOKEN_KEY = environment.auth.cookieKey;
const TOKEN_EXPIRY_KEY = environment.auth.cookieExpiryDateKey;

/**
 * Servicio de autenticación.
 * Gestiona el ciclo de vida del token OAuth2 (obtención, refresco automático, expiración).
 * Expone el token actual como Signal y BehaviorSubject para compatibilidad con RxJS.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(StorageService);

  // --- Estado reactivo ---
  private readonly _accessToken = new BehaviorSubject<string>(
    this.storage.getLocalItem<string>(TOKEN_KEY) ?? '',
  );
  readonly accessToken$ = this._accessToken.asObservable();

  /** Signal del token actual — útil en templates y servicios con Signals */
  readonly accessToken = signal(this._accessToken.getValue());

  readonly isAuthenticated = computed(() => this.accessToken().length > 0);

  private tokenExpiryDate: Date | null = null;
  private tokenExpirationTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshInFlight$: Observable<TokenResponse> | null = null;

  constructor() {
    // Restaurar fecha de expiración si ya hay un token guardado
    const storedExpiry = this.storage.getLocalItem<string>(TOKEN_EXPIRY_KEY);
    if (storedExpiry) {
      this.tokenExpiryDate = new Date(storedExpiry);
    }
  }

  /**
   * Obtiene el token actual como string (para uso síncrono en interceptores)
   */
  getToken(): string {
    return this._accessToken.getValue();
  }

  /**
   * Inicializa la autenticación antes de cada request:
   * - Token válido (>10 min de vida) → retorna inmediatamente
   * - Token expirado / ausente → refresca primero, comparte la petición entre requests concurrentes
   */
  initializeAuth(): Observable<TokenResponse | { access_token: string }> {
    const storedToken = this.storage.getLocalItem<string>(TOKEN_KEY);
    const storedExpiry = this.storage.getLocalItem<string>(TOKEN_EXPIRY_KEY);

    if (storedToken && storedExpiry) {
      this.tokenExpiryDate = new Date(storedExpiry);
      if (!this._accessToken.getValue()) {
        this._accessToken.next(storedToken);
        this.accessToken.set(storedToken);
      }
      if (this.isTokenValid()) {
        return of({ access_token: this._accessToken.getValue() });
      }
    }

    return this.getOrStartRefresh();
  }

  /**
   * Fuerza la obtención de un nuevo token.
   * Comparte el observable entre múltiples llamadas concurrentes (thundering herd prevention).
   */
  fetchToken(): Observable<TokenResponse> {
    const body = new URLSearchParams();
    body.set('grant_type', environment.auth.grantType);
    body.set('scope', environment.auth.scope);
    body.set('client_id', environment.auth.client_id);
    body.set('client_secret', environment.auth.client_secret);

    

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    });

    return this.http
      .post<TokenResponse>(
        `${environment.auth.authBaseDomain}${environment.auth.tokenEndpoint}`,
        body.toString(),
        { headers },
      )
      .pipe(
        tap((response) => this.storeToken(response)),
        catchError((error) => {
          console.error('[AuthService] Error al obtener token:', error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Limpia el token y cancela el timer de renovación
   */
  clearAuth(): void {
    this.storage.removeLocalItem(TOKEN_KEY);
    this.storage.removeLocalItem(TOKEN_EXPIRY_KEY);
    this._accessToken.next('');
    this.accessToken.set('');
    if (this.tokenExpirationTimer) {
      clearTimeout(this.tokenExpirationTimer);
      this.tokenExpirationTimer = null;
    }
    this.tokenExpiryDate = null;
  }

  /**
   * Obtiene los HttpHeaders con autenticación (Bearer token y session_id)
   * Valida que el usuario esté autenticado antes de construir los headers
   * @returns Observable con HttpHeaders incluyendo el Bearer token
   */
  getAuthHeaders(): Observable<HttpHeaders> {
    return this.initializeAuth().pipe(
      map((response) => {
        const token = response.access_token;
        return new HttpHeaders({
          'Accept': '*/*',
          'Authorization': `Bearer ${token}`,
          // 'session_id' es un valor de ejemplo. En un caso real, esto podría ser un ID de sesión generado por el backend o un UUID.
          'session_id': '1dqsqswaear2ewsasdafa3',
        });
      }),
      catchError((error) => {
        console.error('[AuthService] Error obteniendo headers de autenticación:', error);
        return throwError(() => new Error('No autorizado: no se pudo obtener el token'));
      }),
    );
  }

  // --- Métodos privados ---

  private getOrStartRefresh(): Observable<TokenResponse> {
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = this.fetchToken().pipe(
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay(1),
      );
    }
    return this.refreshInFlight$;
  }

  private storeToken(response: TokenResponse): void {
    const expiryDate = new Date(Date.now() + response.expires_in * 1000);
    this.tokenExpiryDate = expiryDate;

    this.storage.setLocalItem(TOKEN_KEY, response.access_token);
    this.storage.setLocalItem(TOKEN_EXPIRY_KEY, expiryDate.toISOString());

    this._accessToken.next(response.access_token);
    this.accessToken.set(response.access_token);

    // Renovar automáticamente 60s antes de la expiración
    this.scheduleTokenRefresh(response.expires_in);
  }

  private scheduleTokenRefresh(expiresIn: number): void {
    if (this.tokenExpirationTimer) {
      clearTimeout(this.tokenExpirationTimer);
    }
    this.tokenExpirationTimer = setTimeout(
      () => this.fetchToken().subscribe(),
      (expiresIn - 60) * 1000,
    );
  }

  private isTokenValid(): boolean {
    if (!this._accessToken.getValue() || !this.tokenExpiryDate) {
      return false;
    }
    const minutesLeft =
      (this.tokenExpiryDate.getTime() - Date.now()) / (1000 * 60);
    return minutesLeft > 10;
  }
}
